import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { useTable, Column } from 'react-table';
import axios from "axios";
import { Link } from "react-router-dom";
import Paging from '../../common/layouts/page/Paging';
import BookDetail from "@/domain/admin/detail/BookDetail";

interface TableData {
    bookTitle: string;
    author?: {
        authorName: string;
    };
}

interface getBookData {
    authorEmail: string;
    bookList?: TableData[];
}

const BookTable = ({ authorEmail, bookList }: getBookData) => {
    const [books, setBooks] = useState<TableData[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const onPageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    useEffect(() => {
        if (bookList !== undefined) {
            setBooks(bookList);
            setTotalPages(1);
            return;
        }

        // 2. 부모 데이터가 없을 경우 API 호출 진행 (어드민 전체 작품 목록 조회)
        const adminToken = sessionStorage.getItem('adminToken');
        if (!adminToken) return;

        axios.post("/api/admin/book/list", {}, {
            headers: {
                Authorization: `Bearer ${adminToken}`,
                'Content-Type': 'application/json'
            },
            params: {
                page: currentPage,
                size: 10,
            },
        })
            .then(response => {
                if (Array.isArray(response.data.content)) {
                    setBooks(response.data.content);
                    setTotalPages(response.data.totalPages || 1);
                } else {
                    setBooks([]);
                }
            })
            .catch(error => {
                console.error("Error fetching books:", error);
            });
    }, [currentPage, authorEmail, bookList]); // bookList가 변경될 때도 트리거

    const columns: Column<TableData>[] = React.useMemo(
        () => [
            {
                Header: '제목',
                accessor: 'bookTitle',
            },
        ],
        []
    );

    const data: TableData[] = useMemo(() => {
        return books.map((book) => ({
            ...book,
            bookTitle: book.bookTitle,
        }));
    }, [books]);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
        columns,
        data,
    });

    return (
        <div className="main-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] overflow-hidden">
                        <div className="card-body p-0">
                            <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minHeight: '120px' }}>
                                <table className="table table-hover mb-0" id="proposalList" {...getTableProps()} style={{ minWidth: '600px' }}>
                                    <thead style={{ backgroundColor: '#f8fafc' }}>
                                    {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" {...column.getHeaderProps()}>{column.render('Header')}</th>
                                            ))}
                                        </tr>
                                    ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()}>
                                    {rows.map(row => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map(cell => {
                                                    return (
                                                        <td className="px-4 py-3" {...cell.getCellProps()} style={{ verticalAlign: 'middle' }}>
                                                            {cell.column.id === 'bookTitle' ? (
                                                                <Link to={`/bookDetail`} state={{ book: row.original }} className="text-decoration-none fw-bold text-[#6366f1] hover:text-[#4f46e5]">
                                                                    {cell.render('Cell')} {row.original.author?.authorName ? `(${row.original.author.authorName})` : ''}
                                                                </Link>
                                                            ) : (
                                                                cell.render('Cell')
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="card-footer bg-white border-top-0 d-flex justify-content-center py-4 px-4">
                            <Paging totalPages={totalPages} currentPage={currentPage} onPageChange={onPageChange} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookTable;
